import { setYaxisRangeBasedOnTracesVisibility, updateYaxisRangeOnZoomWithUniformIntervals } from 'helpers/plotly/yAxisRangeCalculator'

// Axis Names //
export const ACCESSORIES_PLOT_BAND_Y_AXIS_NAME = 'y'
export const ID_PLOT_BAND_Y_AXIS_NAME = 'y2'
export const OD_PLOT_BAND_Y_AXIS_NAME = 'y3'
export const ALERTS_PLOT_BAND_Y_AXIS_NAME = 'y4'
export const TEMP_HUMIDITY_Y_AXIS_NAME = 'y5'
export const AIRFLOW_STEPS_Y_AXIS_NAME = 'y6'

const PLOT_BAND_HEIGHT = 0.06
const PLOT_BAND_PADDING_TOP = 0.005

const getRectShape = ({ axisName, fillcolor, lineWidth }) => {
  return ({
    type: 'rect',
    layer: 'below',
    xref: 'paper',
    yref: `${axisName} domain`,
    x0: 0,
    x1: 1,
    y0: 0,
    y1: 1,
    fillcolor: fillcolor || 'rgba(0,0,0,0)', // Default Transparent
    visible: true,
    line: {
      width: lineWidth,
      color: '#D2D2D2'
    }
  })
}

const getAnnotations = (x, y, yref, text, xshift, textangle) => {
  return {
    xref: 'paper',
    yref,
    x,
    y,
    text,
    align: 'center',
    standoff: 40,
    textangle,
    xshift,
    showarrow: false,
    captureevents: false,
    font: {
      size: 12
    }
  }
}

const getYaxis = (range, domain, side) => {
  return {
    side,
    range,
    domain,
    fixedrange: true,
    showgrid: false,
    showticklabels: false,
    zeroline: false,
    showline: false,
    linecolor: '#939598',
    linewidth: 1,
    anchor: 'x1',
    position: -0.1
  }
}

const accessoriesYaxis = getYaxis([0, 100], [0, 0.06], 'left') // Accessories band Y axis
const indoorYaxis = getYaxis([0, 100], [0.065, 0.125], 'left') // Indoor band Y axis
const outdoorYaxis = getYaxis([0, 100], [0.130, 0.19], 'left') // outdoor band Y axis
const alertsYaxis = getYaxis([0, 100], [0.19, 0.25], 'left') // alerts band Y axis
const tempAndHumidityYAxis = { // TEMP and HUMIDITY Y axis
  ...getYaxis([0, 4], [0.25, 1], 'left'),
  showgrid: true,
  gridcolor: '#D2D2D2',
  automargin: true,
  fixedrange: false,
  showticklabels: true,
  title: 'Temperature °F | Relative Humidity %',
  titlefont: { size: 12 },
  dtick: 1,
  tickcolor: '#D2D2D2',
  ticklen: 2
}
const airflowPressureAndStepsYaxis = { // Right side Y axis
  ...getYaxis([0, 0], [0.25, 1], 'right'),
  showgrid: true,
  gridcolor: '#D2D2D2',
  automargin: true,
  fixedrange: false,
  showticklabels: true,
  dtick: 0,
  overlaying: TEMP_HUMIDITY_Y_AXIS_NAME,
  ticklen: 2,
  tickcolor: '#D2D2D2'
}

const accessoriesAnnotation = getAnnotations(0, 0.5, `${ACCESSORIES_PLOT_BAND_Y_AXIS_NAME} domain`, 'accessories', -75.5, 0)
const indoorAnnotation = getAnnotations(0, 0.5, `${ID_PLOT_BAND_Y_AXIS_NAME} domain`, 'indoor', -44, 0)
const outdoorAnnotation = getAnnotations(0, 0.5, `${OD_PLOT_BAND_Y_AXIS_NAME} domain`, 'outdoor', -53, 0)
const alertsAnnotation = getAnnotations(0, 0.5, `${ALERTS_PLOT_BAND_Y_AXIS_NAME} domain`, 'alerts', -40, 0)
const airflowAndStepsAxisTitleAnnotation = getAnnotations(1, 0.5, `${AIRFLOW_STEPS_Y_AXIS_NAME} domain`, 'Airflow (ft³/min) | Pressure (psig) | Steps', 55, 90)

export const baseLayout = {
  barmode: 'group',
  font: {
    family: 'Inter, sans-serif',
    size: 11,
    color: '#939598'
  },
  showlegend: false,
  autosize: true,
  margin: { t: 50, b: 30, l: 75, r: 75, pad: 0 },
  hovermode: 'x',
  hoverdistance: 50000, // Added as maximum distance to trigger all mouse events properly.
  yaxis: accessoriesYaxis,
  yaxis2: indoorYaxis,
  yaxis3: outdoorYaxis,
  yaxis4: alertsYaxis,
  yaxis5: tempAndHumidityYAxis,
  yaxis6: airflowPressureAndStepsYaxis,
  xaxis: {
    zeroline: false,
    ticklen: 8,
    showgrid: false,
    autorange: false,
    linecolor: '#D2D2D2',
    linewidth: 1,
    nticks: 12,
    tickcolor: '#D2D2D2',
    tickformatstops: [{
      dtickrange: [null, 1000],
      value: '%-I:%M:%S.%L %p'
    },
    {
      dtickrange: [1000, 60000],
      value: '%-I:%M:%S %p'
    },
    {
      dtickrange: [60000, 3600000],
      value: '%-I:%M %p'
    },
    {
      dtickrange: [3600000, 86400000],
      value: '%-I %p'
    }],
    type: 'date'
  },
  grid: {
    rows: 5,
    columns: 1,
    pattern: 'independent',
    roworder: 'bottom to top'
  },
  'xaxis.autorange': false,
  'yaxis.autorange': false,
  shapes: [
    getRectShape({ axisName: ID_PLOT_BAND_Y_AXIS_NAME, fillcolor: '#D2D2D2', lineWidth: 0 }),
    getRectShape({ axisName: OD_PLOT_BAND_Y_AXIS_NAME, lineWidth: 0 }),
    getRectShape({ axisName: TEMP_HUMIDITY_Y_AXIS_NAME, lineWidth: 1 }),
    getRectShape({ axisName: AIRFLOW_STEPS_Y_AXIS_NAME, lineWidth: 1 })
  ],
  annotations: [ // annotations just for displaying the customised titles
    accessoriesAnnotation,
    indoorAnnotation,
    outdoorAnnotation,
    alertsAnnotation,
    airflowAndStepsAxisTitleAnnotation
  ]
}

export const buildLayout = (traces, rawRthData) => {
  const NEGLIGIBLE_HEIGHT = 0.001 // making domain height minimal so that is not visible

  const layout = JSON.parse(JSON.stringify(baseLayout)) // deepclone layout object
  setYaxisRange(traces, layout)

  if (!layout.yaxis6.dtick) { // if data NOT exists for y6 axis Airflow (ft³/min) | Pressure (psig) | Steps
    const annotation = _.find(layout.annotations, (a) => a.text === airflowAndStepsAxisTitleAnnotation.text)
    layout.annotations = _.without(layout.annotations, annotation)
    layout.yaxis6.visible = false
  }

  if (!_.any(traces, (t) => t.yaxis === ACCESSORIES_PLOT_BAND_Y_AXIS_NAME && t.y.length)) {
    const annotation = _.find(layout.annotations, (a) => a.text === accessoriesAnnotation.text)
    layout.annotations = _.without(layout.annotations, annotation) // Does not displays the accessories band title

    layout.yaxis.domain = [0.0, NEGLIGIBLE_HEIGHT]
    layout.yaxis2.domain = [0.006, 0.006 + layout.yaxis.domain[1] + PLOT_BAND_HEIGHT]
  }

  if (rawRthData?.outdoorUnitConfigOccurrences?.length && _.all(rawRthData?.outdoorUnitConfigOccurrences, (occurrence) => occurrence.unitType === 'NONE')) {
    const annotation = _.find(layout.annotations, (a) => a.text === outdoorAnnotation.text)
    layout.annotations = _.without(layout.annotations, annotation) // Does not displays the outdoor band title
    layout.yaxis3.domain = [layout.yaxis2.domain[1] + PLOT_BAND_PADDING_TOP, layout.yaxis2.domain[1] + PLOT_BAND_PADDING_TOP + NEGLIGIBLE_HEIGHT]
  } else {
    layout.yaxis3.domain = [layout.yaxis2.domain[1] + PLOT_BAND_PADDING_TOP, layout.yaxis2.domain[1] + PLOT_BAND_PADDING_TOP + PLOT_BAND_HEIGHT]
  }

  if (!rawRthData?.alarmOccurrences?.length > 0 && !rawRthData?.deviceConnectionStatusOccurrences?.length > 0) {
    const annotation = _.find(layout.annotations, (a) => a.text === alertsAnnotation.text)
    layout.annotations = _.without(layout.annotations, annotation) // Does not displays the outdoor band title
    layout.yaxis4.domain = [layout.yaxis3.domain[1] + PLOT_BAND_PADDING_TOP, layout.yaxis3.domain[1] + PLOT_BAND_PADDING_TOP + NEGLIGIBLE_HEIGHT]
  } else {
    layout.yaxis4.domain = [layout.yaxis3.domain[1] + PLOT_BAND_PADDING_TOP, layout.yaxis3.domain[1] + PLOT_BAND_PADDING_TOP + PLOT_BAND_HEIGHT]
  }
  layout.yaxis5.domain = [layout.yaxis4.domain[1] + 0.02, 1]
  layout.yaxis6.domain = [layout.yaxis4.domain[1] + 0.02, 1]

  return layout
}

export const setYaxisRange = (traces, layout) => {
  setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis5, TEMP_HUMIDITY_Y_AXIS_NAME)
  setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis6, AIRFLOW_STEPS_Y_AXIS_NAME)
}

export const updateYaxisRangeOnZoom = (layout) => {
  if (layout) {
    updateYaxisRangeOnZoomWithUniformIntervals(layout.yaxis5)
    updateYaxisRangeOnZoomWithUniformIntervals(layout.yaxis6)
  }
}

export const zoneColors = [
  'rgba(31, 114, 157, 1.0)', // dark blue (NOTE: must be the first color in the list)
  'rgba(113, 156, 77, 1.0)', // green
  'rgba(252, 54, 180, 1.0)', // pink
  'rgba(246, 162, 0, 1.0)', // orange
  'rgba(0, 206, 155, 1.0)', // green
  'rgba(205, 53, 41, 1.0)', // red
  'rgba(165, 199, 216, 1.0)', // light blue
  'rgba(159, 68, 155, 1.0)', // purple
  'rgba(20, 115, 21, 1.0)' // dark green
]
